import * as React from "react";
import Button from "@mui/material/Button";
import Dialog, { DialogProps } from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { TLCEventManager } from "../events/EventManager";
import { useEffect } from "react";
import { useLanguage } from "./Language";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import Stack from "@mui/material/Stack";
import * as dayjs from "dayjs";
import { Dayjs } from "dayjs";
import { LS2PAC_CONSTANTS } from "../../main/constants";
import { DateValidationError } from "@mui/x-date-pickers/models";
import { PrintAccountDataEvent } from "@tlc/ls2pac/app/managers/PrintAccountDataManager";

interface DateRangeDialogEventData {}

export const DateRangeDialogEvent = "OpenDateRangeDialog";

// TODO datepicker required workaround, set slot.textField.ariaInvalid and slot.textField.helperText? 

export const DateRangeDialog: React.FC<Partial<DialogProps>> = (props) => {
  const theme = useTheme();
  const { translate } = useLanguage();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

  const [open, setOpen] = React.useState<boolean>(props.open || false);

  const startDefault = dayjs().subtract(1, "month");
  const endDefault = dayjs();

  const [startError, setStartError] =
    React.useState<DateValidationError | null>(null);
  const [endError, setEndError] = React.useState<DateValidationError | null>(
    null
  );

  const [startDate, setStartDate] = React.useState<Dayjs | null>(startDefault);
  const [startMaxDate, setStartMaxDate] = React.useState<Dayjs | null>(
    endDefault
  );
  const [endDate, setEndDate] = React.useState<Dayjs | null>(endDefault);
  const [endMinDate, setEndMinDate] = React.useState<Dayjs | null>(
    startDefault
  );

  const startErrorMessage = React.useMemo(() => {
    switch (startError) {
      case "maxDate":
      case "minDate": {
        return translate("InvalidDates");
      }

      case "invalidDate": {
        return translate("invalidDateError");
      }

      case "disableFuture": {
        return translate("disableFutureError");
      }

      default: {
        return "";
      }
    }
  }, [startError]);

  const endErrorMessage = React.useMemo(() => {
    switch (endError) {
      case "maxDate":
      case "minDate": {
        return translate("InvalidDates");
      }

      case "invalidDate": {
        return translate("invalidDateError");
      }

      case "disableFuture": {
        return translate("disableFutureError");
      }

      default: {
        return "";
      }
    }
  }, [endError]);

  useEffect(() => {
    const guid = TLCEventManager.addListener(
      DateRangeDialogEvent,
      (event: DateRangeDialogEventData) => {
        setOpen(true);
      }
    );

    return () => {
      TLCEventManager.removeListener(DateRangeDialogEvent, guid);
    };
  }, []);

  const onChangeEndDate = (newValue: Dayjs) => {
    setEndDate(newValue);
    setStartMaxDate(newValue);
  };

  const onChangeStartDate = (newValue: Dayjs) => {
    setStartDate(newValue);
    setEndMinDate(newValue);
  };

  const handleClose = () => {
    setOpen(false);
    setEndDate(endDefault);
    setStartDate(startDefault);
    setEndMinDate(startDefault);
    setStartMaxDate(endDefault);
  };

  const handlePrint = () => {
    setOpen(false);
    TLCEventManager.fireEvent(PrintAccountDataEvent, {
      sections: [LS2PAC_CONSTANTS.MyAccountPrintTypes.loanHistory],
      startDate: startDate.toDate(),
      endDate: endDate.toDate(),
    });
    setEndDate(endDefault);
    setStartDate(startDefault);
    setEndMinDate(startDefault);
    setStartMaxDate(endDefault);
  };

  return (
    <div>
      <Dialog
        {...props}
        fullScreen={fullScreen}
        open={open}
        onClose={handleClose}
        aria-labelledby="responsive-dialog-title"
      >
        <DialogTitle id="responsive-dialog-title">
          {translate("selectDateRange")}
        </DialogTitle>
        <DialogContent>
          <Stack direction="row" mt={2} spacing={2}>
            <DatePicker
              desktopModeMediaQuery={theme.breakpoints.up("md")}
              autoFocus
              value={startDate}
              maxDate={startMaxDate}
              onChange={onChangeStartDate}
              onError={(newError) => setStartError(newError)}
              slotProps={{
                textField: {
                  helperText: startErrorMessage,
                  required: true,
                },
              }}
              disableFuture
              label={translate("from")}
            />
            <DatePicker
              autoFocus
              desktopModeMediaQuery={theme.breakpoints.up("md")}
              value={endDate}
              onChange={onChangeEndDate}
              onError={(newError) => setEndError(newError)}
              slotProps={{
                textField: {
                  helperText: endErrorMessage,
                  required: true,
                },
              }}
              minDate={endMinDate}
              disableFuture
              label={translate("to")}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={handleClose}>
            {translate("cancel")}
          </Button>
          <Button
            onClick={handlePrint}
            disabled={
              !startDate || !endDate || startError != null || endError != null
            }
            autoFocus
          >
            {translate("ok")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
